/**
 *
 * ModalSignUp
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _get from 'lodash/get';
import { createStructuredSelector } from 'reselect';
import { toast } from 'react-toastify';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import {
  setAuthModalAction,
  checkEmailAvailability,
} from 'containers/Auth/actions';
import { makeSelectEmailAvailability } from 'containers/Auth/selectors';
import { showTermsOfUse, showPrivacyPolicy } from 'containers/Main/actions';
import {
  makeSelectClientDetails,
  makeSelectOAuthOptions,
} from 'containers/Main/selectors';
import SignUpStepOne from './SignUpStepOne';
import SignUpStepTwo from './SignUpStepTwo';

const styles = theme => ({
  container: {
    padding: '16px 32px',
    backgroundColor: 'white',
    width: 500,
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'absolute',
    top: '5%',
    left: '50%',
    marginLeft: -250,
    [theme.breakpoints.down('sm')]: {
      width: 300,
      left: '50%',
      marginLeft: -150,
    },
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerText: {
    color: '#003C29',
    fontSize: 24,
  },
  closeIcon: {
    padding: 6,
  },
  closeImage: {
    width: 24,
    height: 24,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
  },
  dividerItem: {
    flex: 1,
    height: 1,
    backgroundColor: '#CECECE',
  },
  dividerText: {
    color: '#CECECE',
    paddingLeft: 12,
    paddingRight: 12,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
  },
  loginText: {
    fontSize: 16,
  },
  loginButton: {
    background: '#ffffff',
    color: theme.palette.primary.main,
    padding: 12,
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      background: '#f6f6f6',
    },
  },
});

class ModalSignUp extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      step: 0,
      email: '',
      password: '',
      showPassword: false,
      firstName: '',
      lastName: '',
      gender: '',
      age: '',
      marketing: true,
    };
  }

  componentDidUpdate(prevProps) {
    const { emailAvailability } = this.props;

    if (prevProps.emailAvailability === null && emailAvailability !== null) {
      if (emailAvailability.error) {
        toast('Ops. Something went wrong. Please, try again.', {
          type: 'error',
        });
      } else if (emailAvailability.exists) {
        toast('Email is already linked to an account. Try signing in.', {
          type: 'error',
        });
      } else {
        this.toNextStep();
      }
    }
  }

  toNextStep = () => {
    this.setState({
      step: 1,
    });
  };

  togglePasswordVisibility = () => {
    this.setState(prevState => ({
      showPassword: !prevState.showPassword,
    }));
  };

  toggleMarketing = () => {
    this.setState(prevState => ({
      marketing: !prevState.marketing,
    }));
  };

  handleSocialLogin = provider => {
    const { onSignUp } = this.props;
    onSignUp({
      provider: { provider, type: 'popup' },
    });
  };

  handleFormChange = e => {
    this.setState({
      [e.target.name]: e.target.value,
    });
  };

  handleCaptionClick = action => {
    const { doShowTermsOfUse, doShowPrivacyPolicy } = this.props;

    if (action === 'agreement') doShowTermsOfUse(true);
    if (action === 'privacy') doShowPrivacyPolicy(true);
  };

  handleSubmit = e => {
    e.preventDefault();

    const { step } = this.state;
    if (step === 0) {
      const { email } = this.state;
      const { doCheckEmailAvailability } = this.props;
      doCheckEmailAvailability(email);
    } else {
      const { onSignUp } = this.props;
      const {
        email,
        password,
        age,
        firstName,
        lastName,
        gender,
        marketing,
      } = this.state;
      onSignUp({
        data: {
          email,
          password,
          dob: age,
          firstName,
          lastName,
          gender,
          genderOther: null,
          receiveMarketing: marketing,
        },
        provider: null,
      });
    }
  };

  render() {
    const {
      step,
      email,
      password,
      showPassword,
      firstName,
      lastName,
      gender,
      age,
      marketing,
    } = this.state;
    const {
      classes,
      onClose,
      doSetSignUpModal,
      type,
      authOptions,
      clientDetails,
    } = this.props;

    return (
      <Grid container justify="center" className={classes.container}>
        <Grid item xs={12} className={classes.header}>
          <Typography className={classes.headerText}>
            Sign Up to{' '}
            {type === 'topic'
              ? 'Favorite this Topic'
              : `${type.charAt(0).toUpperCase() + type.slice(1)} this Resource`}
          </Typography>
          <IconButton
            className={classes.closeIcon}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Grid>
        {step === 0 && (
          <SignUpStepOne
            authOptions={authOptions}
            email={email}
            password={password}
            showPassword={showPassword}
            customDisclaimer={_get(clientDetails, 'signUpIntro')}
            disclaimerColor={_get(clientDetails, 'primaryColor')}
            onChange={this.handleFormChange}
            onCaptionClick={this.handleCaptionClick}
            onSocialLogin={this.handleSocialLogin}
            onSubmit={this.handleSubmit}
            onTogglePasswordVisibility={this.togglePasswordVisibility}
          />
        )}
        {step === 1 && (
          <SignUpStepTwo
            email={email}
            firstName={firstName}
            lastName={lastName}
            gender={gender}
            age={age}
            marketing={marketing}
            onChange={this.handleFormChange}
            onSubmit={this.handleSubmit}
            onToggleMarketing={this.toggleMarketing}
          />
        )}
        {authOptions.cm && (
          <>
            <Grid item xs={12} className={classes.divider}>
              <div className={classes.dividerItem} />
            </Grid>
            <Grid item xs={12} className={classes.footer}>
              <Typography className={classes.loginText}>
                Already on CredibleMind?
              </Typography>
              <ButtonBase
                className={classes.loginButton}
                onClick={doSetSignUpModal}
              >
                Sign in
              </ButtonBase>
            </Grid>
          </>
        )}
      </Grid>
    );
  }
}

ModalSignUp.propTypes = {
  authOptions: PropTypes.object,
  classes: PropTypes.object,
  clientDetails: PropTypes.object,
  emailAvailability: PropTypes.object,
  onClose: PropTypes.func,
  doCheckEmailAvailability: PropTypes.func,
  doSetSignUpModal: PropTypes.func,
  doShowTermsOfUse: PropTypes.func,
  doShowPrivacyPolicy: PropTypes.func,
  onSignUp: PropTypes.func,
  type: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  authOptions: makeSelectOAuthOptions(),
  clientDetails: makeSelectClientDetails(),
  emailAvailability: makeSelectEmailAvailability(),
});

function mapDispatchToProps(dispatch) {
  return {
    doCheckEmailAvailability: payload =>
      dispatch(checkEmailAvailability(payload)),
    doSetSignUpModal: () => dispatch(setAuthModalAction('sign-in')),
    doShowTermsOfUse: payload => dispatch(showTermsOfUse(payload)),
    doShowPrivacyPolicy: payload => dispatch(showPrivacyPolicy(payload)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  withStyles(styles),
)(ModalSignUp);
